import { buildScopeNameList, scopeMetadataKey } from "./manualEstimatePayload";

// One order entry per selected scope BLOCK, including its cross-structure material/labor.
// The order's structure identifies the block; each line keeps its own product structure.
// Legacy block-0 rows still belong to separate scopes per structure.
export function buildCountertopOrderItems({
  scopes = [],
  installDates = {},
  materialList = [],
  laborList = [],
}) {
  return scopes.map((scope) => {
    const psid = Number(scope.productStructureId) || 0;
    const sbid = Number(scope.scopeBlockId) || 0;
    const key = scope.key || scopeMetadataKey({ scopeBlockId: sbid, productStructureId: psid });
    const installationDate = installDates[key] || "";

    const inScope = (item) =>
      Number(item.scopeBlockId || 0) === sbid &&
      (sbid > 0 || Number(item.productStructureId) === psid);
    const materials = materialList.filter(inScope);
    const labor = laborList.filter(inScope);

    const nameList = buildScopeNameList([...materials, ...labor]).map((entry) => ({
      ...entry,
      installationDate,
    }));
    const firstScope = nameList[0] || {};
    const lineStructure = (item) => {
      const id = Number(item.productStructureId) || psid;
      const name = scope.structures?.find((s) => Number(s.productStructureId) === id)?.structureName ||
        item.structureName || (id === psid ? scope.structureName : "") || `Structure ${id}`;
      return { ProductStructureId: id, structureName: name };
    };

    return {
      ProductStructureId: psid,
      productStructureName: scope.structureName || `Structure ${psid}`,
      scopeBlockId: sbid,
      roomName: scope.roomName || firstScope.roomName || "",
      photoUrl: scope.photoUrl || firstScope.photoUrl || "",
      nameList,
      InstallationDate: installationDate,
      material: materials.map((item) => ({
        itemId: item.isPrimary ? (item.productId || "") : (item.materialId || item.productId || ""),
        quantity: item.quantity ?? "",
        itemType: item.isPrimary ? "Product" : "Material",
        unit: item.unit || item.unitName || "",
        privateName: item.privateName || "",
        style: item.style || item.product || "",
        color: item.color || "",
        GPM: item.gpm ?? 0,
        unitPrice: item.unitPrice ?? item.price ?? "",
        linePrice: item.linePrice ?? "",
        ...lineStructure(item),
        scopeBlockId: item.scopeBlockId ?? 0,
        parentProductId: item.parentProductId || 0,
        parentProductName: item.parentProductName || "",
      })),
      labor: labor.map((item) => ({
        itemId: item.laborId || item.laborItemId || "",
        quantity: item.quantity ?? "",
        itemType: "Labor",
        unit: item.unit || item.unitName || "",
        laborCategory: item.laborCategory || "",
        labor: item.labor || item.laborName || "",
        GPM: item.gpm ?? 0,
        unitPrice: item.unitPrice ?? item.price ?? "",
        linePrice: item.linePrice ?? "",
        ...lineStructure(item),
        scopeBlockId: item.scopeBlockId ?? 0,
        parentProductId: item.parentProductId || 0,
        parentProductName: item.parentProductName || "",
      })),
    };
  });
}

// Build the "Select Area to Order" rows for the Convert to Measure modal — ONE row per scope block.
//
// The block's first primary supplies its scope label. Added items retain their own structures,
// but do not create additional scopes or orders. `structures` supplies names for individual lines.
//
// Rows preserve their `scopeBlockId`, so two blocks that share the same primary structure (e.g. two
// Countertops rooms) remain two distinct rows. `allItems` is the hydrated material + labor list
// (each item carries productStructureId, scopeBlockId, isPrimary, roomName, photoUrl, linePrice).
export function buildScopeBlockRows(allItems = [], structureNameMap = {}) {
  const items = Array.isArray(allItems) ? allItems : [];
  const num = (v) => Number(v) || 0;
  const nameOf = (psid) => {
    const match = items.find((i) => num(i.productStructureId) === psid && i.structureName);
    return structureNameMap[psid] || (match && match.structureName) || `Structure ${psid}`;
  };
  const toStructures = (psids) =>
    psids.map((p) => ({ productStructureId: p, structureName: nameOf(p) }));

  const order = [];
  const byBlock = new Map();
  items.forEach((i) => {
    const sbid = num(i.scopeBlockId);
    const key = sbid > 0 ? `block_${sbid}` : `block_0:${num(i.productStructureId)}`;
    if (!byBlock.has(key)) { byBlock.set(key, []); order.push(key); }
    byBlock.get(key).push(i);
  });

  return order.map((key) => {
    const blockItems = byBlock.get(key);
    const sbid = num(blockItems[0].scopeBlockId);
    const structureIds = [...new Set(blockItems.map((i) => num(i.productStructureId)).filter(Boolean))];
    // The block's ONE MAIN primary structure = the structure of its FIRST primary product (the one the
    // scope was created around). Products ADDED later via "Add New Material Item" are also isPrimary but
    // in their own structures — they must NOT widen the label. Fall back to the first item's structure
    // when no primary exists (e.g. legacy data) so a row is never blank.
    const mainItem = blockItems.find((i) => i.isPrimary === true) || blockItems[0] || {};
    const mainId = num(mainItem.productStructureId);
    const structures = toStructures(structureIds);
    const primaryStructures = mainId ? toStructures([mainId]) : [];
    const meta = blockItems.find((i) => i.roomName || i.photoUrl) || blockItems[0] || {};
    const total = blockItems.reduce((sum, i) => {
      const lp = Number(i.linePrice);
      return sum + (Number.isFinite(lp) ? lp : 0);
    }, 0);
    return {
      key,
      scopeBlockId: sbid,
      productStructureId: mainId,
      structures,
      primaryStructures,
      primaryStructureName: primaryStructures.map((s) => s.structureName).join(", "),
      structureName: primaryStructures[0]?.structureName || "",
      roomName: meta.roomName || "",
      photoUrl: meta.photoUrl || "",
      total,
    };
  });
}

// Resolve a material's structure independently of the containing order's scope label.
// A different known structure ID must never inherit the parent's name when the API omits a label.
export function getOrderLineStructureName(line = {}, orderItem = {}, structureNames = {}) {
  const id = Number(line.productStructureId ?? line.ProductStructureId) || 0;
  const parentId = Number(orderItem.productStructureId ?? orderItem.ProductStructureId) || 0;
  const name = structureNames[id] || line.structureName || line.productStructureName ||
    line.ProductStructureName || line.productStructure || line.productType;
  if (name) return name;
  if (id && id !== parentId) return `Structure ${id}`;
  return orderItem.productStructureName || orderItem.ProductStructureName || "";
}
